import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { secureAxios } from "@/middleware/encryptionMiddleware";

// Updated Fetch all FAQs thunk with proper meta flags
export const fetchFAQsAsync = createAsyncThunk(
  "faq/fetchFAQs",
  async ({ skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      // Make request without authentication
      const response = await secureAxios.get(
        `${BASE_URL}/v1/faq?skip=${skip}&limit=${limit}`
      );


      return response.data;
    } catch (error) {
      console.error("FAQ fetch error:", error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: false, // We're not encrypting the request
      requiresDecryption: true, // The response needs decryption
    },
  }
);

// Fetch single FAQ thunk with proper meta flags
export const fetchSingleFAQAsync = createAsyncThunk(
  "faq/fetchSingleFAQ",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = await secureAxios.get(
        `${BASE_URL}/v1/faq/by-any/${key}/${value}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: false,
      requiresDecryption: true,
    },
  }
);

const initialState = {
  isLoading: false,
  error: null,
  success: false,
  currentFAQ: null,
  faqs: [],
  pagination: {
    skip: 0,
    limit: 10,
    total: 0,
  },
  singleFAQ: null,
  singleFAQLoading: false,
};

const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {
    clearFAQState: (state) => {
      state.error = null;
      state.success = false;
      state.currentFAQ = null;
      state.singleFAQ = null;
    },
    setPagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch FAQs cases
      .addCase(fetchFAQsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFAQsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.faqs = action.payload.data;
        state.pagination.total =
          action?.payload?.total || action.payload?.data?.length;
      })
      .addCase(fetchFAQsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Single FAQ cases
      .addCase(fetchSingleFAQAsync.pending, (state) => {
        state.singleFAQLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleFAQAsync.fulfilled, (state, action) => {
        state.singleFAQLoading = false;
        state.singleFAQ = action.payload.data[0];
      })
      .addCase(fetchSingleFAQAsync.rejected, (state, action) => {
        state.singleFAQLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearFAQState, setPagination } = faqSlice.actions;

// Selectors
export const selectFAQIsLoading = (state) => state.faq.isLoading;
export const selectFAQError = (state) => state.faq.error;
export const selectFAQSuccess = (state) => state.faq.success;
export const selectCurrentFAQ = (state) => state.faq.currentFAQ;
export const selectAllFAQs = (state) => state.faq.faqs;
export const selectFAQPagination = (state) => state.faq.pagination;
export const selectSingleFAQ = (state) => state.faq.singleFAQ;
export const selectSingleFAQLoading = (state) => state.faq.singleFAQLoading;

export default faqSlice.reducer;
